import React from 'react';
import { palette, dimensions, typography } from '../theme';

type PillVariant = 'primary' | 'outline' | 'ghost';
type PillSize = 'XS' | 'S' | 'L';

interface PillProps {
  variant?: PillVariant;
  size?: PillSize;
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}

const HEIGHTS: Record<PillSize, number> = {
  XS: dimensions.controlHeight.xs,
  S: dimensions.controlHeight.sm,
  L: dimensions.controlHeight.lg
};
const FONT_SIZES: Record<PillSize, number> = { XS: 12, S: 14, L: 14 };
const PADDINGS: Record<PillSize, string> = { XS: '4px 12px', S: '8px 16px', L: '8px 16px' };

const SHADOW = `0px 2px 10px 0px ${palette.primitives.purple.purple75}`;

const colorsFor = (variant: PillVariant) => {
  if (variant === 'outline') {
    return {
      background: palette.primitives.base.white,
      border: palette.primitives.purple.purple400,
      color: palette.primitives.purple.purple400,
      boxShadow: SHADOW
    };
  }
  if (variant === 'ghost') {
    return {
      background: palette.primitives.grey.grey25,
      border: palette.primitives.purple.purple400,
      color: palette.primitives.purple.purple400,
      boxShadow: 'none'
    };
  }
  return {
    background: palette.primitives.purple.purple400,
    border: palette.primitives.purple.purple400,
    color: palette.primitives.base.white,
    boxShadow: SHADOW
  };
};

export const Pill: React.FC<PillProps> = ({ variant = 'primary', size = 'L', onClick, disabled, children }) => {
  const c = colorsFor(variant);
  const Tag = onClick ? 'button' : 'div';

  return (
    <Tag
      onClick={disabled ? undefined : onClick}
      disabled={onClick ? disabled : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: dimensions.spacing['2xl'],
        height: HEIGHTS[size],
        padding: PADDINGS[size],
        borderRadius: dimensions.radius.pill,
        border: `1px solid ${c.border}`,
        backgroundColor: c.background,
        color: c.color,
        boxShadow: c.boxShadow,
        fontFamily: typography.fontFamily.ui,
        fontSize: FONT_SIZES[size],
        fontWeight: typography.fontWeight.semibold,
        cursor: onClick ? (disabled ? 'not-allowed' : 'pointer') : 'default',
        opacity: disabled ? 0.5 : 1,
        boxSizing: 'border-box'
      }}
    >
      {children}
    </Tag>
  );
};
